/**
 * 文件说明：定义列表节点 slot 连线写入 edge.data.context 的通用数据结构。
 *
 * 这里不关心 gallery、文本列表、视频列表的具体字段，只描述“这条边是批量语义，
 * 还是引用某些 slot 位置”的业务语义。具体 slot 如何解析，由节点上下文层按 source
 * 节点类型处理。
 */
import type { Edge } from '@xyflow/react'

export type SlotEdgeMode = 'batch' | 'reference'

export type SlotEdgeContext =
  | { mode: 'batch' }
  | { mode: 'reference'; slotIndexes: number[] }

type EdgeDataLike = Edge['data'] | undefined

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function readContextRecord(data: EdgeDataLike) {
  if (!isRecord(data)) {
    return undefined
  }

  return isRecord(data.context) ? data.context : undefined
}

function normalizeSlotIndexes(value: unknown) {
  if (!Array.isArray(value)) {
    return []
  }

  return value.filter((item): item is number => (
    typeof item === 'number' && Number.isInteger(item) && item >= 0
  ))
}

/**
 * 从任意 data 对象中读取 slotIndex。
 *
 * @param data 可能来自 NodeContextPayload.data 或其他 slot 载体的数据
 * @returns 合法的非负整数 slotIndex；不存在或非法时返回 null
 */
export function readSlotIndex(data: unknown) {
  if (!isRecord(data)) {
    return null
  }

  const { slotIndex } = data
  return typeof slotIndex === 'number' && Number.isInteger(slotIndex) && slotIndex >= 0
    ? slotIndex
    : null
}

function normalizeUniqueSlotIndexes(slotIndexes: readonly number[]) {
  return Array.from(new Set(
    slotIndexes.filter((slotIndex) => (
      Number.isInteger(slotIndex) && slotIndex >= 0
    )),
  ))
}

/**
 * 从 edge.data.context 中读取 slot 连线语义。
 *
 * @param data React Flow edge.data
 * @returns 合法的 slot context；不存在或结构非法时返回 undefined
 */
export function readSlotEdgeContextFromData(data: EdgeDataLike): SlotEdgeContext | undefined {
  const context = readContextRecord(data)

  if (!context) {
    return undefined
  }

  if (context.mode === 'batch') {
    return { mode: 'batch' }
  }

  if (context.mode === 'reference') {
    return {
      mode: 'reference',
      slotIndexes: normalizeSlotIndexes(context.slotIndexes),
    }
  }

  return undefined
}

/**
 * 从 edge 上读取 slot 连线语义。
 *
 * @param edge React Flow edge
 * @returns 合法的 slot context；不存在或结构非法时返回 undefined
 */
export function readSlotEdgeContext(edge: Edge): SlotEdgeContext | undefined {
  return readSlotEdgeContextFromData(edge.data)
}

/**
 * 判断 edge 是否是列表 slot 批量语义。
 *
 * @param edge React Flow edge
 * @returns edge.data.context.mode 是否为 batch
 */
export function isBatchSlotEdge(edge: Edge) {
  return readSlotEdgeContext(edge)?.mode === 'batch'
}

/**
 * 判断 edge 是否是列表 slot 引用语义。
 *
 * @param edge React Flow edge
 * @returns edge.data.context.mode 是否为 reference
 */
export function isReferenceSlotEdge(edge: Edge) {
  return readSlotEdgeContext(edge)?.mode === 'reference'
}

/**
 * 构造批量语义 edge.data。
 *
 * @param data 现有 edge.data，保留非 context 的业务字段
 * @returns 写入 batch context 后的新 edge.data
 */
export function withBatchSlotEdgeContext(data?: EdgeDataLike): Edge['data'] {
  return {
    ...(isRecord(data) ? data : {}),
    context: { mode: 'batch' },
  }
}

/**
 * 构造多个 slot 的引用语义 edge.data。
 *
 * @param slotIndexes 当前批次内的 slot 下标列表
 * @param data 现有 edge.data，保留非 context 的业务字段
 * @returns 写入 reference context 后的新 edge.data
 */
export function withReferenceSlotEdgeIndexesContext(slotIndexes: readonly number[], data?: EdgeDataLike): Edge['data'] {
  return {
    ...(isRecord(data) ? data : {}),
    context: {
      mode: 'reference',
      slotIndexes: normalizeUniqueSlotIndexes(slotIndexes),
    },
  }
}

/**
 * 向已有 reference edge 追加一组 slotIndex，并自动去重。
 *
 * @param edge 需要更新的 reference edge
 * @param slotIndexes 当前批次内的 slot 下标列表
 * @returns 带有新 slotIndexes 的 edge；如果 slot 已存在，仍返回结构稳定的新对象
 */
export function appendReferenceSlotIndexes(edge: Edge, slotIndexes: readonly number[]): Edge {
  const context = readSlotEdgeContext(edge)
  const currentSlotIndexes = context?.mode === 'reference' ? context.slotIndexes : []
  const nextSlotIndexes = normalizeUniqueSlotIndexes([...currentSlotIndexes, ...slotIndexes])

  return {
    ...edge,
    data: {
      ...(isRecord(edge.data) ? edge.data : {}),
      context: {
        mode: 'reference',
        slotIndexes: nextSlotIndexes,
      },
    },
  }
}

/**
 * 从已有 reference edge 中移除一个 slotIndex。
 *
 * @param edge 需要更新的 reference edge
 * @param slotIndex 当前批次内的 slot 下标
 * @returns 仍有 slot 时返回更新后的 edge；slot 全部删空时返回 null；非 reference edge 原样返回
 */
export function removeReferenceSlotIndex(edge: Edge, slotIndex: number): Edge | null {
  const context = readSlotEdgeContext(edge)
  if (context?.mode !== 'reference') {
    return edge
  }

  const nextSlotIndexes = context.slotIndexes.filter((item) => item !== slotIndex)
  if (nextSlotIndexes.length === 0) {
    return null
  }

  if (nextSlotIndexes.length === context.slotIndexes.length) {
    return edge
  }

  return {
    ...edge,
    data: {
      ...(isRecord(edge.data) ? edge.data : {}),
      context: {
        mode: 'reference',
        slotIndexes: nextSlotIndexes,
      },
    },
  }
}

/**
 * 将已有 reference edge 的 slot 引用强制替换为新指定的 slot 下标。
 *
 * @param edge 需要更新的 reference edge
 * @param slotIndexes 新的 slot 下标列表
 * @returns 带有新 slotIndexes 的 edge
 */
export function replaceReferenceSlotIndexes(edge: Edge, slotIndexes: readonly number[]): Edge {
  return {
    ...edge,
    data: withReferenceSlotEdgeIndexesContext(slotIndexes, edge.data),
  }
}
